import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";
import { BellRing, Check, ShieldAlert, Clock, Users, CalendarClock, Activity, Trash2 } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

function fmtAge(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${Math.round(s / 3600)}h`;
}

function fmtDur(seconds) {
    if (seconds == null || isNaN(seconds)) return "—";
    const s = Math.round(seconds);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function SummaryCard({ label, value, valueCls, testid }) {
    return (
        <div className="cm-card px-4 py-3" data-testid={testid}>
            <div className="cm-label">{label}</div>
            <div className={`text-2xl font-mono ${valueCls || "text-cm-text"}`}>{value}</div>
        </div>
    );
}

/* ── Tab 1: Open alerts + incidents + silences ───────────────────────────── */
function AlertsTab() {
    const { data, error, mutate } = useSWR("/api/admin/infra/monitoring/open-alerts", fetcher, { refreshInterval: 15000 });
    const { data: incidents } = useSWR("/api/admin/infra/monitoring/incidents?limit=30", fetcher, { refreshInterval: 30000 });
    const { data: silences, mutate: mutSil } = useSWR("/api/admin/infra/monitoring/silences", fetcher, { refreshInterval: 30000 });
    const { data: istats } = useSWR("/api/admin/infra/monitoring/incident-stats", fetcher, { refreshInterval: 60000 });
    const [silForm, setSilForm] = useState({ entity_pattern: "", starts_at: "", ends_at: "", reason: "" });
    const [busy, setBusy] = useState(null);

    const ack = async (entityId) => {
        setBusy(entityId);
        try {
            await api.post(`/api/admin/infra/monitoring/alerts/${encodeURIComponent(entityId)}/ack`, { by: "operator" });
            mutate();
        } catch (e) { /* still */ }
        setBusy(null);
    };

    const addSilence = async () => {
        if (!silForm.entity_pattern.trim()) return;
        setBusy("silence");
        try {
            await api.post("/api/admin/infra/monitoring/silences", silForm);
            setSilForm({ entity_pattern: "", starts_at: "", ends_at: "", reason: "" });
            mutSil();
        } catch (e) { /* still */ }
        setBusy(null);
    };

    const delSilence = async (id) => {
        await api.delete(`/api/admin/infra/monitoring/silences/${id}`);
        mutSil();
    };

    const open = data || [];
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-cm-line">
                <SummaryCard testid="summary-open-alerts" label="Open alerts" value={open.length} valueCls={open.length ? "text-cm-danger" : "text-cm-success"} />
                <SummaryCard testid="summary-incidents" label="Incidents (30d)" value={istats?.incidents ?? "—"} />
                <SummaryCard testid="summary-mtta-mttr" label="Avg MTTA / MTTR" value={`${fmtDur(istats?.mtta_s_avg)} / ${fmtDur(istats?.mttr_s_avg)}`} />
            </div>

            <section data-testid="open-alerts">
                <h3 className="cm-h3 flex items-center gap-1.5"><BellRing size={14} /> Open alerts</h3>
                {error ? <div className="text-cm-danger text-xs font-mono">{formatApiError(error)}</div>
                    : !open.length ? <Empty title="No open alerts — all clear" />
                    : (
                        <div className="cm-table-wrap">
                            <table className="cm-table">
                                <thead><tr>
                                    <th className="cm-th">Entity</th><th className="cm-th">Severity</th>
                                    <th className="cm-th">Since</th><th className="cm-th">Status</th><th className="cm-th"></th>
                                </tr></thead>
                                <tbody>
                                    {open.map((a) => (
                                        <tr key={a._id} className="cm-tr" data-testid={`open-alert-${a.entity_id}`}>
                                            <td className="cm-td font-mono">{a.name}<div className="text-xs text-cm-muted">{a.kind} · {a.entity_id}</div></td>
                                            <td className="cm-td"><span className={`px-2 py-0.5 text-xs font-mono border ${a.severity === "critical" ? "bg-red-950 text-red-300 border-red-900" : "bg-amber-950 text-amber-300 border-amber-900"}`}>{a.severity}</span></td>
                                            <td className="cm-td font-mono text-cm-muted">{fmtAge(a.opened_at)}</td>
                                            <td className="cm-td font-mono">{a.acknowledged ? <span className="text-cm-success">acknowledged</span> : a.escalated_at ? <span className="text-cm-warning">escalated</span> : <span className="text-cm-danger">unacknowledged</span>}</td>
                                            <td className="cm-td">
                                                {!a.acknowledged && (
                                                    <button data-testid={`ack-${a.entity_id}`} onClick={() => ack(a.entity_id)} disabled={busy === a.entity_id}
                                                        className="cm-btn-secondary text-xs flex items-center gap-1"><Check size={12} /> Acknowledge</button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </section>

            <section data-testid="incidents">
                <h3 className="cm-h3 flex items-center gap-1.5"><ShieldAlert size={14} /> Recent incidents</h3>
                <div className="cm-table-wrap">
                    <table className="cm-table">
                        <thead><tr><th className="cm-th">Name</th><th className="cm-th">State</th><th className="cm-th">Started</th><th className="cm-th">MTTA</th><th className="cm-th">MTTR</th></tr></thead>
                        <tbody>
                            {(incidents || []).map((i) => (
                                <tr key={i.id} className="cm-tr">
                                    <td className="cm-td font-mono">{i.name}<div className="text-xs text-cm-muted">{i.kind} · {i.severity}</div></td>
                                    <td className="cm-td font-mono">{i.state}</td>
                                    <td className="cm-td font-mono text-cm-muted">{new Date(i.opened_at).toLocaleString("en-GB")}</td>
                                    <td className="cm-td font-mono">{fmtDur(i.mtta_s)}</td>
                                    <td className="cm-td font-mono">{fmtDur(i.mttr_s)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section data-testid="silences">
                <h3 className="cm-h3 flex items-center gap-1.5"><Clock size={14} /> Maintenance silences</h3>
                <div className="flex flex-wrap gap-2 items-end">
                    <input className="cm-input" placeholder="Entity pattern (e.g. host-on.souvera.work)" value={silForm.entity_pattern}
                        onChange={(e) => setSilForm({ ...silForm, entity_pattern: e.target.value })} />
                    <input className="cm-input" type="datetime-local" value={silForm.starts_at}
                        onChange={(e) => setSilForm({ ...silForm, starts_at: e.target.value })} />
                    <input className="cm-input" type="datetime-local" value={silForm.ends_at}
                        onChange={(e) => setSilForm({ ...silForm, ends_at: e.target.value })} />
                    <input className="cm-input" placeholder="Reason" value={silForm.reason}
                        onChange={(e) => setSilForm({ ...silForm, reason: e.target.value })} />
                    <button className="cm-btn-primary text-xs" disabled={busy === "silence"} onClick={addSilence}>Create silence</button>
                </div>
                <div className="cm-table-wrap mt-3">
                    <table className="cm-table">
                        <thead><tr><th className="cm-th">Pattern</th><th className="cm-th">Window</th><th className="cm-th">Reason</th><th className="cm-th"></th></tr></thead>
                        <tbody>
                            {(silences || []).map((s) => (
                                <tr key={s._id} className="cm-tr">
                                    <td className="cm-td font-mono">{s.entity_pattern}</td>
                                    <td className="cm-td font-mono text-cm-muted">{s.starts_at} → {s.ends_at}</td>
                                    <td className="cm-td">{s.reason}</td>
                                    <td className="cm-td"><button onClick={() => delSilence(s._id)} className="text-cm-danger hover:text-red-200"><Trash2 size={13} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

/* ── Tab 2: On-call ───────────────────────────────────────────────────────── */
function OncallTab() {
    const { data: employees, mutate: mutEmp } = useSWR("/api/admin/infra/oncall/employees", fetcher);
    const { data: shifts, mutate: mutShift } = useSWR("/api/admin/infra/oncall/shifts", fetcher);
    const { data: now } = useSWR("/api/admin/infra/oncall/now", fetcher, { refreshInterval: 30000 });
    const { data: schedule } = useSWR("/api/admin/infra/oncall/schedule?days=7", fetcher);
    const [form, setForm] = useState({ name: "", email: "", phone: "", pushover_user_key: "", active: true });
    const [sform, setSform] = useState({ employee_id: "", start_at: "", end_at: "", note: "" });
    const [busy, setBusy] = useState(null);
    const [msg, setMsg] = useState(null);

    const addEmp = async () => {
        if (!form.name.trim()) return;
        setBusy("emp");
        try {
            await api.post("/api/admin/infra/oncall/employees", form);
            setForm({ name: "", email: "", phone: "", pushover_user_key: "", active: true });
            mutEmp();
            setMsg("Technician added");
        } catch (e) { setMsg(formatApiError(e)); }
        setBusy(null);
    };

    const delEmp = async (id) => { await api.delete(`/api/admin/infra/oncall/employees/${id}`); mutEmp(); mutShift(); };
    const addShift = async () => {
        if (!sform.employee_id || !sform.start_at || !sform.end_at) return;
        setBusy("shift");
        try {
            await api.post("/api/admin/infra/oncall/shifts", sform);
            setSform({ employee_id: "", start_at: "", end_at: "", note: "" });
            mutShift();
        } catch (e) { setMsg(formatApiError(e)); }
        setBusy(null);
    };
    const delShift = async (id) => { await api.delete(`/api/admin/infra/oncall/shifts/${id}`); mutShift(); };
    const testNotify = async () => {
        setBusy("test");
        try {
            const r = await api.post("/api/admin/infra/oncall/test-notify");
            setMsg(r.data?.detail || "Test sent");
        } catch (e) { setMsg(formatApiError(e)); }
        setBusy(null);
    };

    return (
        <div className="space-y-6">
            <div className="cm-card p-4 flex items-center gap-4" data-testid="oncall-now">
                <Activity size={18} className="text-cm-brand" />
                <div>
                    <div className="cm-label">On-call now</div>
                    <div className="font-mono text-cm-text">
                        {(now?.employees || []).map((e) => e.name).join(", ") || "no technicians configured"}
                    </div>
                </div>
                <button className="cm-btn-secondary text-xs ml-auto" disabled={busy === "test"} onClick={testNotify}>
                    {busy === "test" ? "Sending…" : "Notify on-call (test)"}
                </button>
                {msg && <span className="text-xs text-cm-muted">{msg}</span>}
            </div>

            <section>
                <h3 className="cm-h3 flex items-center gap-1.5"><Users size={14} /> Technicians</h3>
                <div className="flex flex-wrap gap-2 items-end mb-3">
                    <input className="cm-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <input className="cm-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <input className="cm-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    <input className="cm-input" placeholder="Pushover user key" value={form.pushover_user_key} onChange={(e) => setForm({ ...form, pushover_user_key: e.target.value })} />
                    <button className="cm-btn-primary text-xs" disabled={busy === "emp"} onClick={addEmp}>Add technician</button>
                </div>
                <div className="cm-table-wrap">
                    <table className="cm-table">
                        <thead><tr><th className="cm-th">Name</th><th className="cm-th">Email</th><th className="cm-th">Phone</th><th className="cm-th">Pushover</th><th className="cm-th">Active</th><th className="cm-th"></th></tr></thead>
                        <tbody>
                            {(employees || []).map((e) => (
                                <tr key={e.id} className="cm-tr" data-testid={`emp-${e.id}`}>
                                    <td className="cm-td font-mono">{e.name}</td>
                                    <td className="cm-td font-mono text-cm-muted">{e.email}</td>
                                    <td className="cm-td font-mono text-cm-muted">{e.phone}</td>
                                    <td className="cm-td font-mono">{e.has_pushover_key ? <span className="text-cm-success">configured</span> : <span className="text-cm-danger">missing</span>}</td>
                                    <td className="cm-td">{e.active ? "yes" : "no"}</td>
                                    <td className="cm-td"><button onClick={() => delEmp(e.id)} className="text-cm-danger hover:text-red-200"><Trash2 size={13} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section>
                <h3 className="cm-h3 flex items-center gap-1.5"><CalendarClock size={14} /> Shift schedule (who is on call when)</h3>
                <div className="flex flex-wrap gap-2 items-end mb-3">
                    <select className="cm-input" value={sform.employee_id} onChange={(e) => setSform({ ...sform, employee_id: e.target.value })}>
                        <option value="">Select technician…</option>
                        {(employees || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    <input className="cm-input" type="datetime-local" value={sform.start_at} onChange={(e) => setSform({ ...sform, start_at: e.target.value })} />
                    <input className="cm-input" type="datetime-local" value={sform.end_at} onChange={(e) => setSform({ ...sform, end_at: e.target.value })} />
                    <input className="cm-input" placeholder="Note (vacation, substitute…)" value={sform.note} onChange={(e) => setSform({ ...sform, note: e.target.value })} />
                    <button className="cm-btn-primary text-xs" disabled={busy === "shift"} onClick={addShift}>Create shift</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    {(schedule?.days || []).slice(0, 7).map((d) => (
                        <div key={d.date} className="cm-card px-3 py-2">
                            <div className="text-xs font-mono text-cm-muted">{d.date}</div>
                            <div className="font-mono text-xs text-cm-text">{(d.employees || []).map((e) => e.name).join(", ") || "—"}</div>
                        </div>
                    ))}
                </div>
                <div className="cm-table-wrap">
                    <table className="cm-table">
                        <thead><tr><th className="cm-th">Technician</th><th className="cm-th">Window</th><th className="cm-th">Note</th><th className="cm-th"></th></tr></thead>
                        <tbody>
                            {(shifts || []).slice(0, 20).map((s) => (
                                <tr key={s.id} className="cm-tr">
                                    <td className="cm-td font-mono">{s.employee_name}</td>
                                    <td className="cm-td font-mono text-cm-muted">{s.start_at} → {s.end_at}</td>
                                    <td className="cm-td">{s.note}</td>
                                    <td className="cm-td"><button onClick={() => delShift(s.id)} className="text-cm-danger hover:text-red-200"><Trash2 size={13} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

/* ── Tab 3: SLA ───────────────────────────────────────────────────────────── */
function SlaTab() {
    const { data: tiers } = useSWR("/api/admin/infra/sla/tiers", fetcher);
    const { data: report, isLoading } = useSWR("/api/admin/infra/sla/report?days=30", fetcher, { refreshInterval: 300000 });

    return (
        <div className="space-y-6">
            <div className="cm-card p-4">
                <div className="cm-label">SLA tiers (Host-On)</div>
                <div className="flex gap-4 mt-2">
                    {Object.entries(tiers?.tiers || {}).map(([k, t]) => (
                        <div key={k} className="font-mono text-sm text-cm-text">{t.label}</div>
                    ))}
                </div>
                <div className="text-xs font-mono text-cm-muted mt-2">
                    Credit calculation: for every full {tiers?.credit_step_pct}% below target → {tiers?.credit_value_pct_per_step}% of the monthly fee (cap {tiers?.credit_cap_pct}%).
                </div>
            </div>
            <section>
                <h3 className="cm-h3">Availability (30 days, rolling)</h3>
                {isLoading ? <Spinner /> : (
                    <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead><tr><th className="cm-th">Cloud</th><th className="cm-th">Uptime</th><th className="cm-th">Target</th><th className="cm-th">SLA</th><th className="cm-th">Samples</th></tr></thead>
                            <tbody>
                                {(report?.clouds || []).map((c) => (
                                    <tr key={c.cloud_id} className="cm-tr" data-testid={`sla-${c.cloud_id}`}>
                                        <td className="cm-td font-mono">{c.domain || c.name}<div className="text-xs text-cm-muted">{c.status}</div></td>
                                        <td className="cm-td font-mono">{c.uptime_pct != null ? `${c.uptime_pct}%` : "—"}</td>
                                        <td className="cm-td font-mono text-cm-muted">{c.target_pct}%</td>
                                        <td className="cm-td">{c.within_sla === false ? <span className="text-cm-danger font-mono">violated</span> : c.within_sla === true ? <span className="text-cm-success font-mono">ok</span> : "—"}</td>
                                        <td className="cm-td font-mono text-cm-muted">{c.samples}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

export default function AlertCenterPage() {
    const [tab, setTab] = useState("alerts");
    const tabs = [
        { key: "alerts", label: "Alerts & Incidents", icon: BellRing },
        { key: "oncall", label: "On-call", icon: Users },
        { key: "sla", label: "SLA", icon: Activity },
    ];
    return (
        <div data-testid="alert-center-page">
            <PageHeader testid="alertcenter-header" title="Alert Center"
                subtitle="Alerts · On-call · SLA — Monitoring v2" />
            <div className="px-4 sm:px-6 pb-6">
                <div className="flex gap-2 mb-4">
                    {tabs.map((t) => (
                        <button key={t.key} data-testid={`ac-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            role="tab"
                            aria-selected={tab === t.key}
                            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border flex items-center gap-2 ${tab === t.key ? "bg-cm-brand text-cm-bg border-cm-brand font-bold" : "bg-cm-bg text-cm-muted border-cm-line2 hover:text-cm-text"}`}>
                            <t.icon size={12} /> {t.label}
                        </button>
                    ))}
                </div>
                {tab === "alerts" && <AlertsTab />}
                {tab === "oncall" && <OncallTab />}
                {tab === "sla" && <SlaTab />}
            </div>
        </div>
    );
}
